/**
 * Escolher a mensagem pronta quando o prazo de 24 h já passou (H05, §8.2).
 *
 * ── Por que diálogo, e não o seletor que havia ────────────────────────────
 *
 * Era um `<select>` no meio do editor, com "nome · idioma" em cada linha. Duas
 * coisas quebravam nele, e as duas aparecem só com catálogo de verdade:
 *
 *   1. uma revenda tem dezenas de modelos aprovados, e `<select>` não busca —
 *      rolar uma lista de quarenta itens atrás de "pos_venda_agendamento" com
 *      o cliente esperando é o oposto de atender;
 *   2. ele ficava disponível ao lado de um campo de texto que também parecia
 *      disponível, e a pessoa escrevia primeiro, tentava enviar, e só aí
 *      descobria que precisava de um modelo.
 *
 * O diálogo resolve o segundo por consequência: o caminho passa a ser um só —
 * escolher o modelo, e só então escrever.
 *
 * ── "Modelo", e não "template" ────────────────────────────────────────────
 *
 * É como a Meta escreve em português no painel do WhatsApp Business
 * ("Modelos de mensagem"), que é onde o administrador da revenda cadastra. O
 * vendedor que perguntar ao administrador vai usar a mesma palavra que ele vê
 * na tela dele.
 *
 * ── Duas abas, porque são dois momentos ──────────────────────────────────
 *
 * "Da organização" é o texto que a revenda escreveu em Configurações: entra no
 * campo, a pessoa ajusta e manda. Vale DENTRO das 24 h.
 *
 * "Modelo aprovado" é o da Meta: é o único que o WhatsApp entrega fora desse
 * prazo, e não passa pelo campo — quem monta o texto é a Meta.
 *
 * Elas convivem na mesma caixa porque, para quem atende, as duas respondem a
 * mesma pergunta: "o que eu mando agora?". Separá-las em dois botões obrigaria
 * a decorar de qual família vem cada texto antes de procurar.
 *
 * A aba que abre PRIMEIRO é a que serve ao momento: dentro da janela, a da
 * organização; fora dela, a da Meta — porque ali a outra não entrega.
 *
 * ── Quem tem a permissão cria aqui mesmo (#1192) ─────────────────────────
 *
 * A saudação que falta aparece no meio do atendimento, com o cliente
 * esperando. Mandar a pessoa às Configurações para cadastrá-la é perder a
 * conversa; o formulário abre na própria aba, e a mensagem nova entra na lista
 * assim que é criada. Quem não tem a permissão não vê o botão.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MensagemPronta } from '@cliqcrm/contracts';
import type { Template } from '../lib/capacidades';
import { Botao, Campo, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  RASCUNHO_VAZIO,
  type RascunhoDeMensagemPronta,
} from '@/entities/atendimento/mensagem-pronta';
import { NovaMensagemPronta } from '@/features/atendimento/criar-mensagem-pronta';

/**
 * A busca casa nome, categoria e idioma — sem acento e sem caixa.
 *
 * Nome de modelo vem em `snake_case` do provedor (`pos_venda_agendamento`), e
 * quem procura digita "pos venda" ou "agendamento". Trocar `_` e `-` por espaço
 * é o que faz os dois acharem a mesma coisa.
 */
export function normalizar(texto: string): string {
  return (
    texto
      .toLowerCase()
      .normalize('NFD')
      // Os acentos que o NFD separou. `\p{Diacritic}` e nao o intervalo
      // literal de combinantes: escrito com os caracteres crus, ele e
      // invisivel no editor, e some numa copia sem ninguem notar.
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[_-]+/g, ' ')
  );
}

export function filtrarModelos(catalogo: readonly Template[], busca: string): readonly Template[] {
  const alvo = normalizar(busca.trim());
  if (alvo === '') return catalogo;

  // Todos os termos, em qualquer ordem: quem digita "agendamento pos" acha
  // "pos_venda_agendamento". Uma busca por substring exata não acharia.
  const termos = alvo.split(/\s+/);
  return catalogo.filter((modelo) => {
    const texto = normalizar(`${modelo.nome} ${modelo.categoria} ${modelo.idioma}`);
    return termos.every((termo) => texto.includes(termo));
  });
}

/** Casa título e texto, sem acento e sem caixa — como a busca de modelos. */
export function filtrarProntas(
  lista: readonly MensagemPronta[],
  busca: string,
): readonly MensagemPronta[] {
  const alvo = normalizar(busca.trim());
  if (alvo === '') return lista;

  const termos = alvo.split(/\s+/);
  return lista.filter((mensagem) => {
    const texto = normalizar(`${mensagem.titulo} ${mensagem.texto}`);
    return termos.every((termo) => texto.includes(termo));
  });
}

type Aba = 'organizacao' | 'meta';

/** Onde está a lista da organização: ainda vindo, falhou, ou chegou. */
export type SituacaoDasProntas = 'carregando' | 'erro' | 'pronta';

export function DialogoDeModelos({
  catalogo,
  prontas,
  situacaoDasProntas = 'pronta',
  aoTentarDeNovo,
  escolhido,
  foraDaJanela = false,
  podeCriar = false,
  rascunho,
  aoMudarRascunho,
  aoEscolher,
  aoUsarPronta,
  aoFechar,
}: {
  catalogo: readonly Template[];
  /** As da organização (H05b). Vazio é o caso comum antes de alguém cadastrar. */
  prontas: readonly MensagemPronta[];
  /**
   * Lista vazia porque ainda não chegou, ou porque a busca falhou, não é "a
   * organização não tem nenhuma": dizer isso seria mentir para quem procura.
   */
  situacaoDasProntas?: SituacaoDasProntas;
  /**
   * Busca a lista outra vez, depois de uma falha. Fechar e abrir a caixa não
   * busca: quem consulta a lista é o editor, que continua montado.
   */
  aoTentarDeNovo?: () => void;
  escolhido: string;
  /** `mensagem_pronta × editar` (#1192): mostra "Nova mensagem pronta" na aba da organização. */
  podeCriar?: boolean;
  /**
   * A mensagem pronta sendo escrita, ou `null` com o formulário fechado.
   *
   * Vem do editor da conversa, que sobrevive ao diálogo: fechar a caixa com Esc
   * ou trocar de aba não joga fora o que estava escrito. Obrigatório, para não
   * existir um segundo caminho que só os testes percorreriam.
   */
  rascunho: RascunhoDeMensagemPronta | null;
  aoMudarRascunho: (rascunho: RascunhoDeMensagemPronta | null) => void;
  /**
   * Fora das 24 h, o texto livre não é entregue.
   *
   * A aba da organização continua VISÍVEL — esconder um caminho faz a pessoa
   * procurá-lo —, e os itens ficam travados com o motivo escrito.
   */
  foraDaJanela?: boolean;
  aoEscolher: (nome: string) => void;
  /** Põe o texto no campo do editor. A pessoa edita e manda como qualquer outro. */
  aoUsarPronta: (texto: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const [busca, definirBusca] = useState('');
  const campo = useRef<HTMLInputElement>(null);
  // Abre na aba que serve ao momento — ver o cabeçalho.
  const [aba, definirAba] = useState<Aba>(foraDaJanela ? 'meta' : 'organizacao');
  const criando = rascunho !== null;
  const encontrados = useMemo(() => filtrarModelos(catalogo, busca), [catalogo, busca]);
  const encontradasProntas = useMemo(() => filtrarProntas(prontas, busca), [prontas, busca]);

  /*
   * Ao fechar o formulário, o foco volta para "Nova mensagem pronta".
   *
   * O formulário some com o foco dentro dele, e o foco cairia no `body`: com o
   * diálogo ainda aberto, o próximo Tab sairia para a página de trás. É pelo
   * contêiner, e não por `ref` no botão, porque o `Botao` não repassa `ref`.
   */
  const areaDeCriar = useRef<HTMLDivElement>(null);
  const devolverFoco = useRef(false);
  useEffect(() => {
    if (criando || !devolverFoco.current) return;
    devolverFoco.current = false;
    areaDeCriar.current?.querySelector('button')?.focus();
  }, [criando]);
  const fecharFormulario = (): void => {
    devolverFoco.current = true;
    aoMudarRascunho(null);
  };

  return (
    <Dialogo
      titulo="Mensagens prontas"
      aoFechar={aoFechar}
      botoes={<Botao onClick={aoFechar}>Fechar</Botao>}
    >
      <div className="abas-de-modelos" role="tablist" aria-label="Origem da mensagem pronta">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'organizacao'}
          className="abas-de-modelos__aba"
          onClick={() => definirAba('organizacao')}
        >
          Da organização
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'meta'}
          className="abas-de-modelos__aba"
          onClick={() => definirAba('meta')}
        >
          Modelo aprovado
        </button>
      </div>

      <p className="dialogo__apoio">
        {aba === 'organizacao'
          ? foraDaJanela
            ? 'Passaram mais de 24 h desde a última mensagem do cliente: fora desse prazo o WhatsApp não entrega texto livre. Use a outra aba.'
            : podeCriar
              ? 'O texto entra no campo e você ajusta antes de enviar.'
              : 'O texto entra no campo e você ajusta antes de enviar. Quem escreve esta lista é quem administra, em Configurações.'
          : 'Aprovados pela Meta. São os únicos que o WhatsApp entrega passadas 24 h da última mensagem do cliente — e o texto sai como foi aprovado.'}
      </p>

      {aba === 'organizacao' &&
        podeCriar &&
        (criando ? (
          <NovaMensagemPronta
            variante="interno"
            focar
            rascunho={rascunho}
            aoMudarRascunho={aoMudarRascunho}
            aoCriar={fecharFormulario}
            aoCancelar={fecharFormulario}
          />
        ) : (
          <div ref={areaDeCriar}>
            <Botao miudo onClick={() => aoMudarRascunho(RASCUNHO_VAZIO)}>
              Nova mensagem pronta
            </Botao>
          </div>
        ))}

      <Campo
        ref={campo}
        rotulo="Buscar"
        type="search"
        value={busca}
        autoFocus
        placeholder={aba === 'organizacao' ? 'título ou texto' : 'nome, categoria ou idioma'}
        onChange={(evento) => definirBusca(evento.target.value)}
      />

      {aba === 'organizacao' ? (
        encontradasProntas.length === 0 ? (
          situacaoDasProntas === 'carregando' ? (
            <p className="dialogo__erro" role="status">
              Buscando as mensagens da organização…
            </p>
          ) : situacaoDasProntas === 'erro' ? (
            <Faixa
              tom="bloqueado"
              {...(aoTentarDeNovo
                ? { acao: { rotulo: 'Tentar de novo', aoClicar: aoTentarDeNovo } }
                : {})}
            >
              Não foi possível buscar as mensagens da organização.
            </Faixa>
          ) : prontas.length > 0 ? (
            <p className="dialogo__erro" role="status">
              {`Nenhuma mensagem pronta com "${busca}".`}
            </p>
          ) : criando ? null : (
            /* Com o formulário aberto, mandar clicar num botão que sumiu seria ruído. */
            <p className="dialogo__erro" role="status">
              {podeCriar
                ? 'Esta organização ainda não tem mensagens prontas. Crie a primeira em "Nova mensagem pronta".'
                : 'Esta organização ainda não tem mensagens prontas. Quem cadastra é quem administra, em Configurações → Mensagens prontas.'}
            </p>
          )
        ) : (
          <ul className="lista-de-modelos">
            {encontradasProntas.map((mensagem) => (
              <li key={mensagem.id}>
                <button
                  type="button"
                  className="lista-de-modelos__item"
                  disabled={foraDaJanela}
                  title={
                    foraDaJanela
                      ? 'Fora das 24 h o WhatsApp não entrega texto livre — use um modelo aprovado.'
                      : 'Põe o texto no campo para você ajustar antes de enviar.'
                  }
                  onClick={() => {
                    aoUsarPronta(mensagem.texto);
                    aoFechar();
                  }}
                >
                  <span className="lista-de-modelos__nome">{mensagem.titulo}</span>
                  {/*
                    O texto inteiro na lista, e não um resumo com reticências.
                    Quem escolhe precisa saber o que vai entrar no campo — e
                    são textos de duas ou três linhas.
                  */}
                  <span className="lista-de-modelos__texto">{mensagem.texto}</span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          {encontrados.length === 0 ? (
            <p className="dialogo__erro" role="status">
              {/*
                Sem canal no texto: desde a #1192 a caixa abre também numa
                conexão sem modelo aprovado (Instagram, um número novo), e
                mandar ao painel do WhatsApp Business ali seria errado.
              */}
              {catalogo.length === 0
                ? 'Esta conexão não tem modelo aprovado. Modelo aprovado existe só no WhatsApp, e quem cadastra é o administrador da organização, no painel do WhatsApp Business.'
                : `Nenhum modelo aprovado com "${busca}".`}
            </p>
          ) : (
            <ul className="lista-de-modelos">
              {encontrados.map((modelo) => (
                <li key={modelo.nome}>
                  <button
                    type="button"
                    className="lista-de-modelos__item"
                    aria-pressed={escolhido === modelo.nome}
                    onClick={() => {
                      aoEscolher(modelo.nome);
                      aoFechar();
                    }}
                  >
                    {/*
                    O nome vem do provedor em `snake_case`. Mostrar com espaço
                    seria mais bonito e erraria: é por este nome, exatamente
                    assim, que o administrador o encontra no painel da Meta.
                  */}
                    <span className="lista-de-modelos__nome">{modelo.nome}</span>
                    <span className="lista-de-modelos__meta">
                      {modelo.categoria} · {modelo.idioma}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Dialogo>
  );
}
